import React, { useCallback, useMemo, useState } from 'react';

const TARGET_PHASES = [
  { key: 'teaser', label: 'Teasers' },
  { key: 'starts_soon', label: 'Countdowns' },
  { key: 'welcome', label: 'Welcome / announcement' },
  { key: 'now_playing', label: 'Now Playing / start' },
  { key: 'finished', label: 'Finish / restoration' },
];

const formatTime = (value) => {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 'Time unavailable' : parsed.toLocaleString();
};

const screenDeviceIdsForSnapshot = (snapshot) => {
  const logicalScreens = Array.isArray(snapshot?.logicalScreens) ? snapshot.logicalScreens : [];
  const screenDeviceIds = logicalScreens
    .map((screen) => Number(screen.routeDeviceId || screen.deviceId))
    .filter(Number.isInteger);
  if (Array.isArray(snapshot?.logicalScreens)) return Array.from(new Set(screenDeviceIds));
  return Array.isArray(snapshot?.deviceIds)
    ? snapshot.deviceIds.map(Number).filter(Number.isInteger)
    : [];
};

export default function ProgrammeAutomationPreview({ gig, occurrence: suppliedOccurrence, siteId, targetGroups = [], token, onChanged }) {
  const [open, setOpen] = useState(false);
  const [occurrenceId, setOccurrenceId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [creatives, setCreatives] = useState([]);
  const [slotPlans, setSlotPlans] = useState({});
  const [busy, setBusy] = useState('');
  const [message, setMessage] = useState('');

  const authHeaders = useMemo(() => ({
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }), [token]);
  const visibleTargetPhases = useMemo(() => TARGET_PHASES.filter((phase) => (
    preview?.items?.some((item) => item.phaseKey === phase.key)
  )), [preview]);

  const loadPreview = useCallback(async () => {
    if (!suppliedOccurrence?.id && (!gig?.id || !siteId)) return;
    setBusy('preview');
    setMessage('');
    try {
      let occurrence = suppliedOccurrence;
      if (!occurrence?.id) {
        const start = new Date(gig.startsAtUtc || gig.starts_at_utc);
        const end = new Date(gig.endsAtUtc || gig.ends_at_utc);
        const params = new URLSearchParams({
          siteId: String(siteId),
          from: new Date(start.getTime() - 60 * 60 * 1000).toISOString(),
          to: new Date(end.getTime() + 60 * 60 * 1000).toISOString(),
        });
        const timelineResponse = await fetch(`/api/venue-programmes/timeline?${params}`, {
          headers: authHeaders,
        });
        const timelineData = await timelineResponse.json();
        if (!timelineResponse.ok) throw new Error(timelineData.message || 'Failed to find programme occurrence');
        occurrence = (timelineData.items || []).find(
          (item) => item.occurrenceKey === `gig:${gig.id}`
        );
      }
      if (!occurrence?.id) throw new Error('Canonical programme occurrence is not available for this gig.');
      const [previewResponse, creativesResponse] = await Promise.all([
        fetch(`/api/venue-programmes/${occurrence.id}/automation/preview`, { headers: authHeaders }),
        fetch(`/api/venue-programmes/${occurrence.id}/creatives`, { headers: authHeaders }),
      ]);
      const [previewData, creativesData] = await Promise.all([
        previewResponse.json(),
        creativesResponse.json(),
      ]);
      if (!previewResponse.ok) throw new Error(previewData.message || 'Failed to preview automation');
      setOccurrenceId(occurrence.id);
      setPreview(previewData);
      setCreatives(creativesResponse.ok && Array.isArray(creativesData.creatives) ? creativesData.creatives : []);
    } catch (error) {
      setMessage(error.message || 'Failed to preview automation.');
    } finally {
      setBusy('');
    }
  }, [authHeaders, gig, siteId, suppliedOccurrence]);

  const togglePreview = async () => {
    const nextOpen = !open;
    setOpen(nextOpen);
    if (nextOpen) await loadPreview();
  };

  const savePhaseTarget = async (phaseKey, targetGroupId) => {
    if (!occurrenceId || !targetGroupId) return;
    setBusy(`target:${phaseKey}`);
    setMessage('');
    try {
      const response = await fetch(
        `/api/venue-programmes/${occurrenceId}/phase-targets/${phaseKey}`,
        {
          method: 'PUT',
          headers: authHeaders,
          body: JSON.stringify({ targetGroupId: Number(targetGroupId) }),
        }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to save target group');
      await loadPreview();
      onChanged?.();
      setMessage(`${TARGET_PHASES.find((phase) => phase.key === phaseKey)?.label || phaseKey} target saved.`);
    } catch (error) {
      setMessage(error.message || 'Failed to save target group.');
    } finally {
      setBusy('');
    }
  };

  const publishAutomation = async () => {
    if (!occurrenceId) return;
    setBusy('publish');
    setMessage('');
    try {
      const response = await fetch(
        suppliedOccurrence?.sourceType === 'campaign'
          ? `/api/venue-programmes/${occurrenceId}/publish`
          : `/api/venue-programmes/${occurrenceId}/automation/publish`,
        { method: 'POST', headers: authHeaders, body: '{}' }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to publish automation');
      await loadPreview();
      const publishedItems = data.items || data.automation?.items || [];
      const publishedCount = publishedItems.filter((item) => item.publishState === 'published').length;
      const draftCount = publishedItems.filter((item) => item.publishState === 'draft').length;
      setMessage(`Published ${publishedCount} managed intentions${draftCount ? `; ${draftCount} still need setup` : ''}.`);
      onChanged?.();
    } catch (error) {
      setMessage(error.message || 'Failed to publish automation.');
    } finally {
      setBusy('');
    }
  };

  const detachItem = async (item) => {
    const timelineItemId = item.relationship?.timelineItemId;
    if (!occurrenceId || !timelineItemId) return;
    setBusy(`detach:${timelineItemId}`);
    setMessage('');
    try {
      const response = await fetch(
        `/api/venue-programmes/${occurrenceId}/timeline-items/${timelineItemId}/detach`,
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({ reason: 'Detached from the Scheduler automation preview' }),
        }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to detach timeline item');
      await loadPreview();
      setMessage(`${item.title} is detached and will no longer be reconciled automatically.`);
    } catch (error) {
      setMessage(error.message || 'Failed to detach timeline item.');
    } finally {
      setBusy('');
    }
  };

  const previewScreenSlots = async (item) => {
    const deviceIds = screenDeviceIdsForSnapshot(item.targetSnapshot);
    if (!deviceIds.length) return;
    setBusy(`slots:${item.ruleKey}`);
    try {
      const slotPolicy = item.intentPayload?.overlaySlot || {};
      const requestedSlot = slotPolicy.requestedSlot || item.intentPayload?.creative?.format || 'full';
      const response = await fetch('/api/overlay-slot-plan', {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          deviceIds,
          start_time: item.scheduledForUtc,
          end_time: item.endsAtUtc || new Date(new Date(item.scheduledForUtc).getTime() + 30 * 1000).toISOString(),
          requestedSlot,
          compatibleSlots: slotPolicy.compatibleSlots || (requestedSlot === 'full'
            ? ['full']
            : [requestedSlot, requestedSlot === 'top' ? 'bottom' : 'top']),
          priority: slotPolicy.priority,
          exclusive: slotPolicy.exclusive ?? requestedSlot === 'full',
          overridePolicy: slotPolicy.overridePolicy || 'never',
          source: 'programme',
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to preview Screen slots');
      setSlotPlans((current) => ({ ...current, [item.ruleKey]: data.devices || [] }));
    } catch (error) {
      setSlotPlans((current) => ({
        ...current,
        [item.ruleKey]: [{ error: error.message || 'Failed to preview Screen slots' }],
      }));
    } finally {
      setBusy('');
    }
  };

  const previewWelcomeAnnouncement = async (item) => {
    const text = item.intentPayload?.announcement?.text;
    if (!text) return;
    setBusy(`welcome:${item.ruleKey}`);
    setMessage('');
    try {
      const response = await fetch('/api/tts', {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ text, languageCode: 'en-GB', voiceName: 'en-GB-Neural2-B' }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to generate welcome preview');
      }
      const objectUrl = URL.createObjectURL(await response.blob());
      const audio = new Audio(objectUrl);
      audio.addEventListener('ended', () => URL.revokeObjectURL(objectUrl), { once: true });
      audio.addEventListener('error', () => URL.revokeObjectURL(objectUrl), { once: true });
      await audio.play();
      setMessage(`Previewing: “${text}”`);
    } catch (error) {
      setMessage(error.message || 'Failed to preview welcome announcement.');
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="mt-3 rounded-xl bg-black/20 p-3 ring-1 ring-white/10">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-xs font-semibold text-cyan-100">Generated timeline</div>
          <div className="text-[11px] text-slate-400">Preview targets and timing before publishing managed intentions.</div>
        </div>
        <button
          type="button"
          onClick={togglePreview}
          disabled={busy === 'preview'}
          className="rounded-lg bg-cyan-500/15 px-2.5 py-1.5 text-xs font-semibold text-cyan-100 hover:bg-cyan-500/25 disabled:opacity-50"
        >
          {busy === 'preview' ? 'Loading…' : open ? 'Close preview' : 'Review automation'}
        </button>
      </div>

      {open && preview ? (
        <div className="mt-3 grid gap-3">
          <div className="grid gap-2 md:grid-cols-2">
            {visibleTargetPhases.map((phase) => {
              const current = preview.items.find((item) => item.phaseKey === phase.key)?.targetGroupId || '';
              return (
                <label key={phase.key} className="grid gap-1 text-[11px] text-slate-300">
                  <span>{phase.label}</span>
                  <select
                    value={current}
                    disabled={busy === `target:${phase.key}`}
                    onChange={(event) => savePhaseTarget(phase.key, event.target.value)}
                    className="rounded-lg bg-slate-950/80 px-2 py-2 text-xs text-white ring-1 ring-white/10"
                  >
                    <option value="">Choose target group…</option>
                    {targetGroups.map((group) => (
                      <option key={group.id} value={group.id}>{group.name}</option>
                    ))}
                  </select>
                </label>
              );
            })}
          </div>

          <div className="grid gap-1.5">
            {preview.items.map((item) => (
              <div key={item.ruleKey} className="rounded-lg bg-white/5 px-2.5 py-2 text-xs ring-1 ring-white/10">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <span className="font-semibold text-white">{item.title}</span>
                    <span className="ml-2 text-slate-400">{formatTime(item.scheduledForUtc)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] ${
                      item.relationship?.detached
                        ? 'bg-amber-500/15 text-amber-100'
                        : item.publishable
                          ? 'bg-emerald-500/15 text-emerald-100'
                          : 'bg-slate-500/20 text-slate-300'
                    }`}>
                      {item.relationship?.detached
                        ? 'Detached'
                        : item.relationship?.managementState === 'customised'
                          ? 'Customised'
                          : item.relationship?.publishState || (item.publishable ? 'Ready' : 'Needs setup')}
                    </span>
                    {item.relationship?.managementState === 'generated' ? (
                      <button
                        type="button"
                        onClick={() => detachItem(item)}
                        disabled={busy === `detach:${item.relationship.timelineItemId}`}
                        className="rounded bg-white/10 px-2 py-0.5 text-[10px] text-slate-200 hover:bg-white/20"
                      >
                        Detach
                      </button>
                    ) : null}
                  </div>
                </div>
                {item.warnings?.length ? (
                  <div className="mt-1 text-[11px] text-amber-200">{item.warnings.join(' ')}</div>
                ) : null}
                {item.intentionType === 'announcement' && item.intentPayload?.announcement?.text ? (
                  <div className="mt-2 rounded-lg bg-cyan-500/10 p-2 text-[11px] text-cyan-50">
                    <div>“{item.intentPayload.announcement.text}”</div>
                    <button
                      type="button"
                      onClick={() => previewWelcomeAnnouncement(item)}
                      disabled={busy === `welcome:${item.ruleKey}`}
                      className="mt-1.5 rounded bg-cyan-500/20 px-2 py-1 text-[10px] font-semibold hover:bg-cyan-500/30 disabled:opacity-50"
                    >
                      {busy === `welcome:${item.ruleKey}` ? 'Preparing voice…' : 'Preview welcome voice'}
                    </button>
                  </div>
                ) : null}
                {item.intentionType === 'overlay' && screenDeviceIdsForSnapshot(item.targetSnapshot).length ? (
                  <div className="mt-2">
                    <button
                      type="button"
                      onClick={() => previewScreenSlots(item)}
                      disabled={busy === `slots:${item.ruleKey}`}
                      className="rounded bg-cyan-500/15 px-2 py-1 text-[10px] text-cyan-100 hover:bg-cyan-500/25 disabled:opacity-50"
                    >
                      {busy === `slots:${item.ruleKey}` ? 'Checking…' : 'Check Screen slots'}
                    </button>
                    {slotPlans[item.ruleKey]?.length ? (
                      <div className="mt-1.5 grid gap-1">
                        {slotPlans[item.ruleKey].map((entry, index) => (
                          <div key={entry.deviceId || index} className="text-[10px] text-slate-300">
                            Screen {entry.deviceId || index + 1}: {entry.error || `${entry.decision?.disposition || 'unknown'}${entry.decision?.selectedSlot ? ` in ${entry.decision.selectedSlot}` : ''} · ${entry.decision?.reason || 'No reason returned'}`}
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ))}
          </div>

          <details className="rounded-lg bg-white/5 p-2.5 ring-1 ring-white/10">
            <summary className="cursor-pointer text-xs font-semibold text-white">
              Creative pack ({creatives.length})
            </summary>
            <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {creatives.map((creative) => (
                <div key={creative.id} className="rounded-lg bg-black/20 p-2 text-[11px] text-slate-300">
                  <div className="font-semibold text-cyan-100">
                    {creative.phaseKey} · {creative.format}
                  </div>
                  {creative.overlay?.imageUrl ? (
                    <img
                      src={creative.overlay.imageUrl}
                      alt={`${creative.phaseKey} ${creative.format} preview`}
                      className="mt-1.5 aspect-video w-full rounded object-cover"
                    />
                  ) : (
                    <div className="mt-1.5 line-clamp-3 min-h-[2.5rem] rounded bg-white/5 p-2">
                      {creative.overlay?.message || 'Generated overlay'}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </details>

          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-[11px] text-slate-400">
              Publishing records intent only. It does not claim that a Screen displayed it or audio played.
            </div>
            <button
              type="button"
              onClick={publishAutomation}
              disabled={busy === 'publish'}
              className="rounded-lg bg-emerald-600/80 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-600 disabled:opacity-50"
            >
              {busy === 'publish' ? 'Publishing…' : 'Publish managed timeline'}
            </button>
          </div>
        </div>
      ) : null}

      {open && message ? <div className="mt-2 text-xs text-amber-100">{message}</div> : null}
    </div>
  );
}
